/**
 * InstallChip — small premium "Install app" pill for mobile headers.
 *
 * Renders nothing unless:
 *   • Viewport is mobile.
 *   • The app is not already installed.
 *   • The browser supports install (native prompt available OR iOS).
 *
 * Two visual variants match the two header surfaces:
 *   • `dark`  — for the marketing nav on a dark background (default).
 *   • `deep`  — for the app shell's deep-navy mobile bar (identical
 *               palette; separate variant left for future tuning).
 *
 * Tap behaviour lives in the provider (`useInstallState().promptInstall`):
 *   • Android / Chromium → fires the native install prompt.
 *   • iOS                → opens the Add-to-Home-Screen sheet.
 */

import { Download } from "lucide-react";
import { useInstallState } from "./InstallPrompt";

export default function InstallChip({
  variant = "dark",
  className = "",
  testid = "install-chip",
}) {
  const { canInstall, promptInstall } = useInstallState();
  if (!canInstall) return null;

  // Palette-tuned per surface. Both stay inside the navy + champagne gold
  // brand tokens — this is a nav element, not an advert.
  const chip =
    variant === "deep"
      ? "text-[#0B1220] bg-gradient-to-b from-[#E6BE7A] to-[#D4A65A] " +
        "hover:from-[#F0D497] hover:to-[#D4A65A] border-[#D4A65A]/70 " +
        "shadow-[0_6px_18px_-8px_rgba(212,166,90,0.65)]"
      : "text-[#0B1220] bg-gradient-to-b from-[#E6BE7A] to-[#D4A65A] " +
        "hover:from-[#F0D497] hover:to-[#D4A65A] border-[#D4A65A]/70 " +
        "shadow-[0_6px_18px_-8px_rgba(212,166,90,0.55)]";

  return (
    <button
      type="button"
      onClick={promptInstall}
      data-testid={testid}
      aria-label="Install CanniComply on this device"
      className={
        "md:hidden inline-flex items-center gap-1.5 h-9 px-2.5 rounded-lg " +
        "border text-[11.5px] font-semibold tracking-[0.02em] " +
        "transition-colors focus:outline-none focus-visible:ring-2 " +
        "focus-visible:ring-[#F0D497]/60 " +
        chip +
        (className ? " " + className : "")
      }
    >
      <Download size={13} strokeWidth={2.2} />
      Install app
    </button>
  );
}
